import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col, InputGroup } from 'react-bootstrap';
import { FiArrowLeft, FiLink, FiCopy, FiCheck } from 'react-icons/fi';
import { generateRibFormUrl } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import 'bootstrap/dist/css/bootstrap.css';

const AdminTripsRibForm = () => {
    const navigate = useNavigate();
    const [policyCode, setPolicyCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ribUrl, setRibUrl] = useState(null);
    const [ribInfo, setRibInfo] = useState(null);
    const [copied, setCopied] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!policyCode.trim()) {
            setError('Le code de police est requis');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setRibUrl(null);
            setRibInfo(null);
            const data = await generateRibFormUrl(policyCode.trim());
            setRibUrl(data?.url || data?.rib_form_url || null);
            setRibInfo(data || null);
        } catch (err) {
            setError(handleApiError(err, 'Erreur lors de la génération du lien RIB'));
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (ribUrl) {
            try {
                await navigator.clipboard.writeText(ribUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                setError('Impossible de copier le lien dans le presse-papiers');
            }
        }
    };

    const handleOpenLink = () => {
        if (ribUrl) {
            window.open(ribUrl, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="dashboard-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="page-title">
                            <FiLink className="me-2" />
                            Générer le lien RIB
                        </h2>
                        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </div>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Code de police <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={policyCode}
                                        onChange={(e) => setPolicyCode(e.target.value)}
                                        placeholder="Ex: 123456789"
                                        required
                                        disabled={loading}
                                    />
                                    <Form.Text className="text-muted">
                                        Le type de distribution est déterminé automatiquement depuis la sous-couverture du séjour.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex gap-2">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={loading || !policyCode.trim()}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        Génération...
                                    </>
                                ) : (
                                    <>
                                        <FiLink className="me-2" />
                                        Générer le lien
                                    </>
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={() => {
                                    setPolicyCode('');
                                    setRibUrl(null);
                                    setRibInfo(null);
                                    setError(null);
                                }}
                                disabled={loading}
                            >
                                Réinitialiser
                            </Button>
                        </div>
                    </Form>

                    {ribUrl && (
                        <Card className="mt-4" style={{ backgroundColor: '#f8f9fa' }}>
                            <Card.Body>
                                <h5 className="mb-3">
                                    <FiLink className="me-2" />
                                    Lien RIB généré
                                </h5>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        value={ribUrl}
                                        readOnly
                                        style={{ backgroundColor: 'white' }}
                                    />
                                    <Button
                                        variant="outline-primary"
                                        onClick={handleCopy}
                                        title="Copier le lien"
                                    >
                                        {copied ? (
                                            <>
                                                <FiCheck className="me-2" />
                                                Copié !
                                            </>
                                        ) : (
                                            <>
                                                <FiCopy className="me-2" />
                                                Copier
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleOpenLink}
                                        title="Ouvrir le lien dans un nouvel onglet"
                                    >
                                        <FiLink className="me-2" />
                                        Ouvrir
                                    </Button>
                                </InputGroup>

                                {ribInfo && (
                                    <>
                                        <Row className="mt-3">
                                            <Col md={4}>
                                                <div className="mb-2">
                                                    <strong>Code police :</strong>
                                                    <div>{ribInfo.policy_code || policyCode}</div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="mb-2">
                                                    <strong>Réservation :</strong>
                                                    <div>{ribInfo.reservation_number || 'N/A'}</div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="mb-2">
                                                    <strong>Type de distribution :</strong>
                                                    <div>{ribInfo.distribution_type || 'N/A'}</div>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row className="mt-2">
                                            <Col md={6}>
                                                <div className="mb-2">
                                                    <strong>Partenaire :</strong>
                                                    <div>
                                                        {ribInfo.company_name
                                                            || ribInfo.partner?.company_name
                                                            || `${ribInfo.partner?.partner_first_name || ''} ${ribInfo.partner?.partner_last_name || ''}`.trim()
                                                            || 'N/A'}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="mb-2">
                                                    <strong>Client :</strong>
                                                    <div>
                                                        {`${ribInfo.first_name || ''} ${ribInfo.last_name || ''}`.trim() || 'N/A'}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                )}

                                <Alert variant="info" className="mt-3 mb-0">
                                    <strong>Note :</strong> Ce lien contient les paramètres nécessaires pour accéder au formulaire RIB de remboursement.
                                </Alert>
                            </Card.Body>
                        </Card>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsRibForm;
